import { useEffect, useState, type FormEvent } from 'react';
import { updateOrderNotes, type Order } from '@/services/orders';
import { fetchOrderPayments, recordOrderPayment, type Payment, METHOD_LABELS } from '@/services/payments';
import { useAuth } from '@/context/AuthContext';
import { isOperationsRole } from '@/lib/rbac';

export const PRODUCTION_STATUSES: Record<string, { label: string; color: string; bg: string }> = {
  para_produccion: { label: 'Para producción', color: 'text-amber-700', bg: 'bg-amber-50' },
  producido: { label: 'Producido', color: 'text-indigo-600', bg: 'bg-indigo-50' },
  listo_envio: { label: 'Listo para enviar', color: 'text-cyan-600', bg: 'bg-cyan-50' },
  entregado: { label: 'Entregado', color: 'text-emerald-700', bg: 'bg-emerald-50' },
  cancelled: { label: 'Cancelado', color: 'text-red-600', bg: 'bg-red-50' },
};

interface ProductionOrderModalProps {
  order: Order;
  onClose: () => void;
  onChanged: () => void;
  onStatusChange: (id: string, status: string) => Promise<void>;
}

export default function ProductionOrderModal({
  order,
  onClose,
  onChanged,
  onStatusChange,
}: ProductionOrderModalProps) {
  const { profile } = useAuth();
  const isOperations = isOperationsRole(profile?.role ?? '');
  const [notes, setNotes] = useState(order.notes ?? '');
  const [status, setStatus] = useState(order.status);
  const [savingNotes, setSavingNotes] = useState(false);
  const [msg, setMsg] = useState('');
  const [err, setErr] = useState('');
  const [payments, setPayments] = useState<Payment[]>([]);
  const [loadingPayments, setLoadingPayments] = useState(true);
  const [amount, setAmount] = useState('');
  const [method, setMethod] = useState('cash');
  const [payStatus, setPayStatus] = useState('approved');
  const [ref, setRef] = useState('');
  const [savingPay, setSavingPay] = useState(false);

  const paid = payments
    .filter((p) => p.status === 'approved')
    .reduce((s, p) => s + p.amount, 0);
  const pending = Math.max(0, order.total - paid);

  async function loadPayments() {
    setLoadingPayments(true);
    const list = await fetchOrderPayments(order.id);
    setPayments(list);
    setLoadingPayments(false);
  }

  useEffect(() => {
    loadPayments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [order.id]);

  async function handleSaveNotes(e: FormEvent) {
    e.preventDefault();
    setSavingNotes(true);
    setErr('');
    setMsg('');
    const { error } = await updateOrderNotes(order.id, notes);
    setSavingNotes(false);
    if (error) {
      setErr(error);
      return;
    }
    setMsg('Notas guardadas.');
    onChanged();
  }

  async function handleStatusChange(s: string) {
    setErr('');
    setMsg('');
    setStatus(s);
    await onStatusChange(order.id, s);
    onChanged();
  }

  async function handleAddPayment(e: FormEvent) {
    e.preventDefault();
    const amt = Number(amount);
    if (!amt || amt <= 0) {
      setErr('Ingresa un monto válido.');
      return;
    }
    setSavingPay(true);
    setErr('');
    setMsg('');
    const { error } = await recordOrderPayment({
      orderId: order.id,
      amount: amt,
      method,
      status: payStatus,
      transactionRef: ref,
    });
    setSavingPay(false);
    if (error) {
      setErr(error);
      return;
    }
    setAmount('');
    setRef('');
    setMsg('Abono registrado.');
    loadPayments();
    onChanged();
  }

  const inputCls =
    'w-full px-3 py-2 text-sm border border-background-200 rounded-md bg-white text-foreground-800 focus:outline-none focus:ring-2 focus:ring-accent-300';
  const labelCls = 'block text-xs font-medium text-foreground-600 mb-1.5';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40" onClick={onClose}></div>

      <div className="relative w-full max-w-2xl max-h-[90vh] bg-white rounded-xl border border-background-200 overflow-hidden flex flex-col">
        <div className="flex items-start justify-between px-5 py-4 border-b border-background-100">
          <div>
            <h2 className="text-base font-semibold text-foreground-950">
              Pedido {order.orderNumber}
            </h2>
            <p className="text-xs text-foreground-500 mt-0.5">
              {order.customerName || '—'}
              {order.customerPhone && ` · ${order.customerPhone}`}
              {order.customerCity && ` · ${order.customerCity}`}
            </p>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-full text-foreground-500 hover:bg-background-100 transition-colors cursor-pointer"
            aria-label="Cerrar"
          >
            <i className="ri-close-line text-lg"></i>
          </button>
        </div>

        <div className="overflow-y-auto px-5 py-5 space-y-5">
          {msg && (
            <div className="flex items-center gap-2 text-sm text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-md px-3 py-2.5">
              <i className="ri-check-line"></i>
              {msg}
            </div>
          )}
          {err && (
            <div className="flex items-start gap-2 text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2.5">
              <i className="ri-error-warning-line mt-0.5"></i>
              <span>{err}</span>
            </div>
          )}

          {/* Estado */}
          <div>
            <label className={labelCls}>Estado</label>
            <select
              className={inputCls}
              value={status}
              onChange={(e) => handleStatusChange(e.target.value)}
            >
              {Object.entries(PRODUCTION_STATUSES).map(([key, s]) => (
                <option key={key} value={key}>
                  {s.label}
                </option>
              ))}
            </select>
            {status === 'entregado' && (
              <p className="text-xs text-foreground-500 mt-1.5 flex items-center gap-1.5">
                <i className="ri-information-line"></i>
                Al entregar, el pedido se convierte en venta y registra su donación.
              </p>
            )}
          </div>

          {/* Productos */}
          <div>
            <label className={labelCls}>Productos</label>
            <div className="space-y-2">
              {order.items.map((item) => (
                <div
                  key={item.id}
                  className="flex items-center justify-between border border-background-200 rounded-md px-3 py-2.5"
                >
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-foreground-900 truncate">
                      {item.productName}
                    </p>
                    <p className="text-xs text-foreground-500">
                      {item.sizeLabel} × {item.quantity}
                    </p>
                  </div>
                  <span className="text-sm font-semibold text-foreground-950 whitespace-nowrap">
                    ${(item.unitPrice * item.quantity).toLocaleString()}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* Pagos */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className={labelCls}>Pagos</label>
              <div className="text-xs text-foreground-500">
                Pagado <span className="font-semibold text-emerald-600">${paid.toLocaleString()}</span>
                {' · '}
                Pendiente <span className="font-semibold text-amber-600">${pending.toLocaleString()}</span>
              </div>
            </div>

            {loadingPayments ? (
              <p className="text-sm text-foreground-500 flex items-center gap-1.5">
                <i className="ri-loader-4-line animate-spin"></i>
                Cargando pagos...
              </p>
            ) : payments.length === 0 ? (
              <p className="text-sm text-foreground-500">Sin pagos registrados todavía.</p>
            ) : (
              <div className="space-y-1.5 mb-3">
                {payments.map((p) => (
                  <div
                    key={p.id}
                    className="flex items-center justify-between border border-background-200 rounded-md px-3 py-2 text-sm"
                  >
                    <div className="min-w-0">
                      <p className="text-foreground-900 font-medium">
                        ${p.amount.toLocaleString()}
                      </p>
                      <p className="text-xs text-foreground-500">
                        {METHOD_LABELS[p.method] ?? p.method}
                        {p.transactionRef && ` · ${p.transactionRef}`}
                      </p>
                    </div>
                    <span
                      className={`text-xs px-2 py-0.5 rounded-full font-medium whitespace-nowrap ${
                        p.status === 'approved'
                          ? 'bg-emerald-100 text-emerald-700'
                          : 'bg-amber-100 text-amber-700'
                      }`}
                    >
                      {p.status === 'approved' ? 'Aprobado' : 'Pendiente'}
                    </span>
                  </div>
                ))}
              </div>
            )}

            {!isOperations && (
            <form onSubmit={handleAddPayment} className="bg-background-50 rounded-lg p-3 space-y-2">
              <p className="text-xs font-semibold text-foreground-700">Registrar abono</p>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="block text-[10px] font-medium text-foreground-500 mb-1">Monto ($)</label>
                  <input
                    type="number"
                    min="0"
                    className={inputCls}
                    value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                    placeholder="0"
                  />
                </div>
                <div>
                  <label className="block text-[10px] font-medium text-foreground-500 mb-1">Método</label>
                  <select className={inputCls} value={method} onChange={(e) => setMethod(e.target.value)}>
                    {Object.entries(METHOD_LABELS).map(([key, label]) => (
                      <option key={key} value={key}>
                        {label}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-[10px] font-medium text-foreground-500 mb-1">Estado</label>
                  <select className={inputCls} value={payStatus} onChange={(e) => setPayStatus(e.target.value)}>
                    <option value="approved">Aprobado</option>
                    <option value="pending">Pendiente</option>
                  </select>
                </div>
                <div>
                  <label className="block text-[10px] font-medium text-foreground-500 mb-1">Referencia</label>
                  <input
                    type="text"
                    className={inputCls}
                    value={ref}
                    onChange={(e) => setRef(e.target.value)}
                    placeholder="Opcional"
                  />
                </div>
              </div>
              <button
                type="submit"
                disabled={savingPay}
                className="w-full text-sm px-4 py-2.5 bg-accent-500 text-white rounded-md hover:bg-accent-600 transition-colors whitespace-nowrap cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {savingPay ? (
                  <>
                    <i className="ri-loader-4-line animate-spin mr-1.5"></i>Guardando...
                  </>
                ) : (
                  <>
                    <i className="ri-add-line mr-1.5"></i>Registrar abono
                  </>
                )}
              </button>
            </form>
            )}
          </div>

          {/* Notas */}
          <div>
            <label className={labelCls}>Notas</label>
            <textarea
              className={`${inputCls} resize-none`}
              rows={3}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Instrucciones de producción, plazos, etc."
              readOnly={isOperations}
              disabled={isOperations}
            />
            {!isOperations && (
              <button
                type="button"
                onClick={handleSaveNotes}
                disabled={savingNotes}
                className="mt-2 text-sm px-4 py-2 border border-background-300 text-foreground-700 rounded-md hover:bg-background-100 transition-colors whitespace-nowrap cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {savingNotes ? 'Guardando...' : 'Guardar notas'}
              </button>
            )}
          </div>
        </div>

        <div className="flex items-center justify-between px-5 py-4 border-t border-background-100">
          <span className="text-sm text-foreground-500">Total</span>
          <span className="text-lg font-bold text-foreground-950">${order.total.toLocaleString()}</span>
        </div>
      </div>
    </div>
  );
}